// Coerces whatever the overview source returns into the exact shape the overview
// components expect (documented in services/competitionOverviewService.js).
//
// The overview is meant to be filled from an API later, for any competition, and
// that data will be parsed from a PDF, so it cannot be trusted to be complete or
// well-typed. Everything here is defensive on purpose: a missing field becomes
// null or an empty list (and its section hides itself), a wrong type is dropped,
// and the page never throws on data it did not expect.

const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const arr = (v) => (Array.isArray(v) ? v : []);
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : null);
const positiveNumber = (v) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : null);
const dateOnly = (v) => {
  const s = str(v);
  return s && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null;
};

const normalizeStages = (value) => {
  const seen = new Set();
  return arr(value)
    .map(obj)
    .filter(Boolean)
    .map((s, i) => {
      const number = positiveNumber(s.number) || i + 1;
      let key = s.key != null && String(s.key).trim() ? String(s.key).trim() : `stage-${number}`;
      // Keys identify a stage (current stage, React keys), so they must be unique.
      if (seen.has(key)) key = `${key}-${i}`;
      seen.add(key);

      const deadline = obj(s.deadline);
      return {
        key,
        number,
        name: str(s.name) || `Stage ${number}`,
        isPlaceholderName: s.isPlaceholderName === true,
        deadline: { date: dateOnly(deadline?.date), isPlaceholder: deadline?.isPlaceholder === true },
        description: str(s.description),
        includes: arr(s.includes).map(str).filter(Boolean),
        deliverables: arr(s.deliverables)
          .map(obj)
          .filter(Boolean)
          .map((d) => ({ title: str(d.title), requirement: str(d.requirement), note: str(d.note) }))
          .filter((d) => d.title),
      };
    });
};

const normalizeRoadmap = (value) => {
  const r = obj(value);
  if (!r) return null;
  const item = (v) => {
    const o = obj(v);
    const title = str(o?.title);
    const text = str(o?.text);
    return title || text ? { title, text } : null;
  };
  const out = { now: item(r.now), afterReview: item(r.afterReview), then: item(r.then) };
  return out.now || out.afterReview || out.then ? out : null;
};

export const normalizeCompetitionOverview = (raw, templateId) => {
  const o = obj(raw) || {};
  const website = obj(o.officialWebsite);
  const nextDeadline = obj(o.nextOfficialDeadline);

  return {
    templateId,
    name: str(o.name),
    tagline: str(o.tagline),
    focus: str(o.focus),
    participation: str(o.participation),
    eligibility: str(o.eligibility),
    officialWebsite: str(website?.url) ? { label: str(website.label), url: str(website.url) } : null,
    currentStageKey: o.currentStageKey != null && String(o.currentStageKey).trim()
      ? String(o.currentStageKey).trim()
      : null,
    nextOfficialDeadline: nextDeadline
      ? {
          date: dateOnly(nextDeadline.date),
          stageLabel: str(nextDeadline.stageLabel),
          isPlaceholder: nextDeadline.isPlaceholder === true,
        }
      : null,
    stages: normalizeStages(o.stages),
    rules: arr(o.rules)
      .map(obj)
      .filter(Boolean)
      .map((r) => ({ label: str(r.label), text: str(r.text) }))
      .filter((r) => r.label),
    links: arr(o.links)
      .map(obj)
      .filter(Boolean)
      .map((l) => ({ label: str(l.label), url: str(l.url) }))
      .filter((l) => l.label && l.url),
    process: arr(o.process)
      .map(obj)
      .filter(Boolean)
      .map((p) => ({
        step: positiveNumber(p.step),
        owner: str(p.owner) ? str(p.owner).toLowerCase() : null,
        title: str(p.title),
        text: str(p.text),
      }))
      .filter((p) => p.title),
    roadmap: normalizeRoadmap(o.roadmap),
  };
};
